import React, { useState } from "react";
import { connect } from "react-redux";
import * as actions from "../../../store/actions/auth";
import LiveDayLaborCore from "./LiveDayLaborCore";
import LiveLaborConfirmModal from "./LiveLaborConfirmModal";
import LiveLaborAlertToggle from "./LiveLaborAlertToggle";
import CloseIcon from "@mui/icons-material/Close";

// Renders the Live Labor report in one of two modes:
//   - normal (kiosk=false): a standard in-app tab; the sidebar and Ops
//     Assistant stay visible and the session is untouched. An "Enter Kiosk
//     Mode" button switches to the kiosk tab.
//   - kiosk (kiosk=true): a full-screen takeover for shared iPads. MainDisplay
//     purges the persisted auth on entry, so the only way out is the "Exit"
//     button, which logs the user out.
function LiveDayLaborDisplay(props) {
  const { kiosk } = props;
  const [openExitModal, setOpenExitModal] = useState(false);
  const [openKioskModal, setOpenKioskModal] = useState(false);

  const storeId = props.homeStore;
  const storeObj = props.storeNames?.find((s) => s.id === storeId);

  // Self-contained report with data fetching — shared by both modes.
  const core = (
    <LiveDayLaborCore
      kiosk={!!kiosk}
      storeId={storeId}
      homeCompany={props.homeCompany ?? storeObj?.company}
      startQhnum={storeObj?.start_qhnum ?? 0}
      storeName={storeObj?.store_name}
      businessWeekStartDay={storeObj?.business_week_start_day}
      storeTimezone={storeObj?.timezone}
      hidePrint
      showActivityFeed
      dailyPlanEnabled={props.dailyPlanEnabled}
      pinRequired={!!storeObj?.kiosk_task_pin_required}
    />
  );

  if (kiosk) {
    return (
      <div className="h-screen bg-white relative overflow-auto">
        {/* Top-right buttons */}
        <div className="absolute top-4 right-6 z-50 flex gap-3">
          <LiveLaborAlertToggle storeId={storeId} />
          <div
            className="px-5 py-1.5 rounded-md cursor-pointer bg-violet-700 hover:bg-violet-900"
            onClick={() => setOpenExitModal(true)}
          >
            <p className="text-white text-lg font-semibold">Exit</p>
          </div>
        </div>

        {core}

        {/* Exit confirmation — exiting logs the user out */}
        <LiveLaborConfirmModal
          open={openExitModal}
          handleClose={() => setOpenExitModal(false)}
          onConfirm={() => props.logout()}
          tone="rose"
          title="Are you sure?"
          message="Exiting will log you out and require another log in"
          confirmLabel="Exit app"
        />
      </div>
    );
  }

  // Normal in-app tab view
  return (
    <div className="bg-white h-screen relative overflow-auto">
      {/* Top-right buttons */}
      <div className="absolute top-4 right-6 z-50 flex gap-3">
        <LiveLaborAlertToggle storeId={storeId} />
        <button
          className="px-4 py-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-semibold shadow-sm whitespace-nowrap"
          onClick={() => setOpenKioskModal(true)}
        >
          Enter Kiosk Mode
        </button>
        {props.onClose && (
          <button
            type="button"
            aria-label="Back to schedule"
            title="Back to schedule"
            className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800 shadow-sm flex items-center justify-center"
            onClick={props.onClose}
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        )}
      </div>

      {core}

      {/* Kiosk-entry confirmation — entering requires logging back in to leave */}
      <LiveLaborConfirmModal
        open={openKioskModal}
        handleClose={() => setOpenKioskModal(false)}
        onConfirm={() => {
          setOpenKioskModal(false);
          props.setCurrentTab("liveDayLaborKiosk");
        }}
        tone="amber"
        title="Enter Kiosk Mode?"
        message="Kiosk mode takes over the full screen for shared devices. You will need to log back in to leave it and access the rest of your account."
        confirmLabel="Enter Kiosk Mode"
      />
    </div>
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default connect(null, mapDispatchToProps)(LiveDayLaborDisplay);
